import React, { useState, useEffect } from 'react';
import { FaCopy, FaTimes } from 'react-icons/fa';
import { PATHS } from '../../routes/routes';
import { HOST_API } from '../../api/config';

interface CopyLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  drawId: string;
  isPromo?: boolean;
}

interface DrawLink {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  customPrice?: number;
  customCurrency?: string;
  customTicketCount?: number;
}

interface DrawLinkOption {
  id: string;
  name: string;
  slug: string;
  url: string;
  displayName: string;
}

const CopyLinkModal: React.FC<CopyLinkModalProps> = ({ 
  isOpen, 
  onClose, 
  drawId, 
  isPromo = false 
}) => {
  const [links, setLinks] = useState<DrawLinkOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && drawId) {
      loadDrawLinks();
    }
  }, [isOpen, drawId]);

  const loadDrawLinks = async () => {
    try {
      setLoading(true);
      console.log('CopyLinkModal: Loading draw links for drawId:', drawId);
      // Use public draw API to get draw info including links
      const response = await fetch(`${HOST_API}/public/club-owner/draw/${drawId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch draw data');
      }
      const drawData = await response.json();
      const drawLinks: DrawLink[] = drawData.links || [];
      console.log('CopyLinkModal: Loaded drawLinks:', drawLinks);
      
      // Create the default link option
      const defaultUrl = isPromo 
        ? PATHS.PROMO_DRAW_SIGNUP.replace(':drawId', drawId)
        : PATHS.DRAW_INFO.replace(':drawId', drawId);
      
      const defaultOption: DrawLinkOption = {
        id: 'default',
        name: 'Default Link',
        slug: '',
        url: defaultUrl,
        displayName: 'Default Draw Link'
      };

      // Create options for each tracking link
      // All tracking links use the same /draw/:slug route regardless of promo status
      const trackingOptions: DrawLinkOption[] = drawLinks
        .filter((link: DrawLink) => link.isActive)
        .map((link: DrawLink) => ({
          id: link.id,
          name: link.name,
          slug: link.slug,
          url: isPromo 
            ? PATHS.PROMO_DRAW_LINK.replace(':drawId', drawId).replace(':slug', link.slug)
            : PATHS.DRAW_INFO_LINK.replace(':drawId', drawId).replace(':slug', link.slug),
          displayName: `${link.name} (/${link.slug})`
        }));

      setLinks([defaultOption, ...trackingOptions]);
    } catch (error) {
      console.error('Error loading draw links:', error);
      // Fallback to default link only
      const defaultUrl = isPromo 
        ? PATHS.PROMO_DRAW_SIGNUP.replace(':drawId', drawId)
        : PATHS.DRAW_INFO.replace(':drawId', drawId);
      
      setLinks([{
        id: 'default',
        name: 'Default Link',
        slug: '',
        url: defaultUrl,
        displayName: 'Default Draw Link'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLinkClick = async (link: DrawLinkOption) => {
    try {
      const origin = window.location.origin;
      const fullUrl = origin + link.url;
      
      // Use the clipboard API directly
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      // Set the copied state for this specific link
      setCopiedLinkId(link.id);
      setTimeout(() => {
        setCopiedLinkId(null);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Copy Draw Link</h3>
          <button 
            onClick={onClose}
            className="btn btn-sm btn-ghost"
          >
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="loading loading-spinner loading-md"></div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 mb-4">
              Choose which link to copy to clipboard:
            </p>
            
            {links.map((link) => (
              <div 
                key={link.id}
                className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors ${
                  copiedLinkId === link.id ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{link.displayName}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {window.location.origin}{link.url}
                  </div>
                </div>
                
                <button
                  onClick={() => handleCopyLinkClick(link)}
                  disabled={copiedLinkId === link.id}
                  className={`btn btn-sm ml-3 ${
                    copiedLinkId === link.id
                      ? 'btn-success text-white'
                      : 'btn-primary'
                  }`}
                >
                  {copiedLinkId === link.id ? (
                    <>
                      <FaCopy className="inline w-3 h-3 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <FaCopy className="inline w-3 h-3 mr-1" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && links.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No links available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CopyLinkModal;